import React, { useEffect, useState } from "react"
import {
    PricingPackageSelector,
    componentStyles,
    useColors,
} from "@mallardbay/lib-react-components"

import PackageSectionsTitle from "~components/pages/listing/pricing/package-sections-title"
import { scrollToElementWithId } from "~utils/helpers"
import type { PricingPackageFieldsFragment } from "~graphql/generated/graphql"
import { navigateToCheckoutPage } from "~utils/navigation-helpers"
import { COMPONENT_IDS } from "~config/constants"
import {
    trackAddToCart,
    trackBeginCheckout,
} from "~utils/event-tracking/event-tracking-helpers"
import { useCreateBookingStartedEvent } from "~utils/event-hooks"
import Box from "~components/shared/todo-lib-react-components/box"
import { useMobileMediaQueries } from "~components/shared/todo-lib-react-components/hooks/use-media-queries"
import PackageContinueButton from "~components/pages/listing/pricing/package-continue-button"
import NoPackagesCard from "~components/pages/listing/pricing/no-packages-card"
import usePricingPackagesByListing from "~utils/use-pricing-packages-by-listing"
import Spinner from "~components/shared/todo-lib-react-components/spinner"
import NoAvailabilityCard from "~components/pages/listing/pricing/no-availability-card"

interface Props {
    readonly outfitterName?: string
    readonly listingId?: string
    readonly published?: boolean
    readonly dates?: number[]
    readonly numberOfGuests?: number
    readonly hideContinueButton?: boolean
    readonly defaultSelectedPackageId?: string | null
    readonly onSelectPackage?: (
        pricingPackage: PricingPackageFieldsFragment
    ) => void
    readonly hasNextAvailableDate?: boolean
    readonly isUnavailable?: boolean
    readonly renderBottomContent?: () => React.ReactNode
}

export default function PackageSections({
    outfitterName,
    listingId,
    published,
    dates,
    numberOfGuests,
    hideContinueButton,
    defaultSelectedPackageId,
    hasNextAvailableDate,
    isUnavailable,
    onSelectPackage,
    renderBottomContent,
}: Props) {
    // Storing pricing packages in state using the query's onCompleted in order to
    // prevent the list from going empty and showing the loading spinner every time a
    // package is selected (which causes the network-only query to re-fire).
    const [pricingPackages, setPricingPackages] = useState<
        PricingPackageFieldsFragment[]
    >([])

    const { isMobileOnly } = useMobileMediaQueries()
    const styles = useStyles({
        isMobileOnly,
        hasBottomContent: Boolean(renderBottomContent),
    })
    const { createBookingStartedEvent } = useCreateBookingStartedEvent()
    const [activePackageId, setActivePackageId] = useState(
        defaultSelectedPackageId
    )
    const [selectedPackage, setSelectedPackage] =
        useState<PricingPackageFieldsFragment>()
    const { isLoading } = usePricingPackagesByListing({
        listingId,
        dates,
        numberOfGuests,
        onCompleted: setPricingPackages,
    })

    const handleSelectPackage = (
        pricingPackage: PricingPackageFieldsFragment
    ) => {
        setActivePackageId(pricingPackage.id)
        setSelectedPackage(pricingPackage)
        onSelectPackage?.(pricingPackage)
        scrollToElementWithId(COMPONENT_IDS.REPORT_LISTING, true, "nearest")
    }

    useAutoSelectOnlyPackage({
        packages: pricingPackages,
        selectedPackage: selectedPackage,
        handleSelectPackage,
        hasAvailability: hasNextAvailableDate,
    })

    // We only want to show loading if there are no packages. This prevents showing the loading spinner
    // after selecting a package (selecting a package updates the number of guests which causes the
    // packages query to fire again in order to update availability props).
    if (isLoading && !pricingPackages.length) return <Spinner noPadding />

    const hasPackages = Boolean(pricingPackages.length)

    const handleContinueClick = () => {
        void createBookingStartedEvent({
            listingId: listingId!,
            pricingPackageIds: [activePackageId!],
        })

        onContinue({
            activePackageId: activePackageId!,
            selectedPackage: selectedPackage!,
            listingId,
        })
    }

    const isPackageContinueButtonDisabled =
        !published || !activePackageId || !selectedPackage

    return (
        <Box style={styles.root}>
            <Box style={styles.content}>
                <NoPackagesCard hasPackages={hasPackages} />
                {hasPackages && (
                    <Box style={styles.cardWrapper}>
                        <PackageSectionsTitle outfitterName={outfitterName} />
                        <PricingPackageSelector
                            pricingPackages={pricingPackages}
                            selectedPricingPackage={selectedPackage}
                            onSelect={handleSelectPackage}
                            hasAvailability={hasNextAvailableDate}
                        />
                        <PackageContinueButton
                            hideContinueButton={hideContinueButton}
                            handleContinueClick={handleContinueClick}
                            isMobile={isMobileOnly}
                            isDisabled={isPackageContinueButtonDisabled}
                        />
                        <NoAvailabilityCard
                            hasAvailability={!isUnavailable}
                            hasPackages={hasPackages}
                        />
                    </Box>
                )}
            </Box>
            {renderBottomContent?.()}
        </Box>
    )
}

function useAutoSelectOnlyPackage({
    packages,
    selectedPackage,
    handleSelectPackage,
    hasAvailability,
}: {
    packages: (PricingPackageFieldsFragment | undefined)[]
    selectedPackage?: PricingPackageFieldsFragment
    handleSelectPackage: (selectedPackage: PricingPackageFieldsFragment) => void
    hasAvailability?: boolean
}) {
    useEffect(() => {
        if (
            packages.length === 1 &&
            packages[0] &&
            !selectedPackage &&
            hasAvailability
        ) {
            handleSelectPackage(packages[0])
        }
        // only select if pachages change, not if a user selects a package
    }, [packages])
}

function onContinue({
    listingId,
    activePackageId,
    selectedPackage,
}: {
    listingId?: string
    activePackageId: string
    selectedPackage: PricingPackageFieldsFragment
}) {
    trackBeginCheckout({ pricingPackage: selectedPackage })
    trackAddToCart(selectedPackage)
    navigateToCheckoutPage({
        listingId: listingId ?? "",
        pricingPackageId: activePackageId,
    })
}

function useStyles({
    isMobileOnly,
    hasBottomContent,
}: {
    isMobileOnly: boolean
    hasBottomContent: boolean
}) {
    const colors = useColors()
    const justifyContent = "flex-start"

    return componentStyles({
        root: {
            marginBottom: isMobileOnly ? "6rem" : "1rem",
        },
        content: {
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent,
            alignItems: "center",
            boxSizing: "border-box",
            marginBottom: hasBottomContent ? 6 : 0,
        },
        cardWrapper: {
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent,
            gridGap: "16px",
            boxSizing: "inherit",
            padding: "1rem",
            backgroundColor: colors.bgColor,
            border: `1px solid ${colors.borderColor}`,
            borderRadius: "10px",
            boxShadow: "rgba(0, 0, 0, 0.08) 0px 1px 12px",
            overflow: "hidden",
        },
    })
}
